import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/ClientLayout';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Route, MapPin, MoreHorizontal, Play, CheckCircle, Zap } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import axios from 'axios';

const RoutesPage = () => {
  const { token, API } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    crew_id: '',
    job_ids: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [routesRes, jobsRes, crewsRes] = await Promise.all([
        axios.get(`${API}/routes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs?status=scheduled`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/crews`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setRoutes(routesRes.data.routes);
      setJobs(jobsRes.data.jobs);
      setCrews(crewsRes.data.crews);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.date) {
      toast.error('Name and date are required');
      return;
    }

    try {
      await axios.post(`${API}/routes`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create route');
    }
  };

  const handleOptimize = async (routeId) => {
    try {
      await axios.post(`${API}/routes/${routeId}/optimize`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Route optimized!');
      fetchData();
    } catch (error) {
      toast.error('Failed to optimize route');
    }
  };

  const handleStatusChange = async (routeId, newStatus) => {
    try {
      await axios.put(`${API}/routes/${routeId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Route ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update route');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', date: '', crew_id: '', job_ids: [] });
  };

  const getCrewName = (id) => crews.find(c => c.id === id)?.name || 'Unassigned';
  const unassignedJobs = jobs.filter(j => !j.route_id);

  const filteredRoutes = routes.filter(r =>
    r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const config = {
      planned: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-amber-100 text-amber-800',
      completed: 'bg-emerald-100 text-emerald-800'
    };
    return <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${config[status] || config.planned}`}>{status}</span>;
  };

  return (
    <ClientLayout title="Routes">
      <Card className="border-gray-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search routes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-routes-input"
              />
            </div>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="add-route-btn">
                  <Plus className="h-4 w-4 mr-2" /> Create Route
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Create New Route</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label>Route Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="North Zone - Monday"
                      data-testid="route-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Date *</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        data-testid="route-date-input"
                      />
                    </div>
                    <div>
                      <Label>Assign Crew</Label>
                      <Select 
                        value={formData.crew_id} 
                        onValueChange={(value) => setFormData({ ...formData, crew_id: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select crew" />
                        </SelectTrigger>
                        <SelectContent>
                          {crews.map((crew) => (
                            <SelectItem key={crew.id} value={crew.id}>{crew.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Assign Jobs ({unassignedJobs.length} unassigned)</Label>
                    <div className="max-h-40 overflow-y-auto border rounded-lg p-2 mt-1 space-y-2">
                      {unassignedJobs.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-2">No unassigned jobs</p>
                      ) : (
                        unassignedJobs.map((job) => (
                          <label key={job.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.job_ids.includes(job.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({ ...formData, job_ids: [...formData.job_ids, job.id] });
                                } else {
                                  setFormData({ ...formData, job_ids: formData.job_ids.filter(id => id !== job.id) });
                                }
                              }}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{job.scheduled_date} - Job #{job.id.slice(0, 8)}</span>
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                  <Button className="w-full bg-primary hover:bg-primary/90" onClick={handleSubmit} data-testid="save-route-btn">
                    Create Route
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredRoutes.length === 0 ? (
            <div className="text-center py-12">
              <Route className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No routes found</p>
              <Button variant="outline" onClick={() => setDialogOpen(true)}>
                Create Your First Route
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRoutes.map((route) => (
                <div 
                  key={route.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                  data-testid={`route-${route.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">{route.name}</h3>
                      <p className="text-sm text-gray-500">{route.date}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOptimize(route.id)}>
                          <Zap className="h-4 w-4 mr-2" /> Optimize Route
                        </DropdownMenuItem>
                        {route.status === 'planned' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(route.id, 'in_progress')}>
                            <Play className="h-4 w-4 mr-2" /> Start Route
                          </DropdownMenuItem>
                        )}
                        {route.status === 'in_progress' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(route.id, 'completed')}>
                            <CheckCircle className="h-4 w-4 mr-2" /> Complete Route
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Crew:</span>
                      <span className="font-medium">{route.crew_id ? getCrewName(route.crew_id) : 'Unassigned'}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Jobs:</span>
                      <span className="font-medium">{route.job_ids?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Status:</span>
                      {getStatusBadge(route.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </ClientLayout>
  );
};

export default RoutesPage;
